import { useEffect } from 'react';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { useNavigate, useLocation } from 'react-router-dom';
import logger from '../utils/logger';

interface HardwareBackButtonOptions {
  enabled?: boolean;
  customHandler?: () => void | boolean; // Return true to prevent default
  exitOnRoot?: boolean; // Exit app when on root page
}

/**
 * Hook for handling hardware back button on Android devices
 */
export const useHardwareBackButton = (options: HardwareBackButtonOptions = {}) => {
  const {
    enabled = true,
    customHandler,
    exitOnRoot = true
  } = options;

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Only enable on Android platform
    const platform = Capacitor.getPlatform();
    if (!enabled || platform !== 'android') {
      return;
    }

    const handleBackButton = () => {
      logger.info('Hardware back button pressed');

      // If custom handler is provided and returns true, prevent default
      if (customHandler) {
        const handled = customHandler();
        if (handled === true) {
          return;
        }
      }

      // Check if we're at the root of the app
      const rootPaths = ['/', '/home', '/feed', '/training', '/profile', '/more'];
      const isRoot = rootPaths.includes(location.pathname);

      if (isRoot && exitOnRoot) {
        // Exit the app
        logger.info('Exiting app from root path');
        App.exitApp();
      } else {
        // Navigate back
        logger.info('Navigating back');
        navigate(-1);
      }
    };

    // Add listener for back button
    const backButtonListener = App.addListener('backButton', handleBackButton);

    // Cleanup
    return () => {
      backButtonListener.then(listener => listener.remove());
    };
  }, [enabled, customHandler, exitOnRoot, navigate, location.pathname]);
};

export default useHardwareBackButton;